import React from 'react';
import { Users, Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Alert from '../common/Alert.js';
import userService from '../../services/UserService.js';
import axiosInstance from '../../config/axios.js';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const UserList = () => {
  const [users, setUsers] = React.useState([]);
  const [roles, setRoles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [filterValues, setFilterValues] = React.useState({
    search: '',
    status: '',
    role_id: '',
  });
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const perPage = 10;

  const canManageUsers = ['Admin', 'System Admin', 'Accountant'].includes(currentUser?.role);

  React.useEffect(() => {
    fetchRoles();
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  React.useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage]);

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles');
      setRoles(response.data.data);
    } catch (err) {
      console.error('Failed to load roles', err);
    }
  };

  const fetchUsers = async (page = currentPage) => {
    try {
      setLoading(true);
      const filters = {};
      if (filterValues.search) filters.search = filterValues.search;
      if (filterValues.status) filters.status = filterValues.status;
      if (filterValues.role_id) filters.role_id = filterValues.role_id;

      const response = await userService.getAll(page, perPage, filters);
      setUsers(response.data);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId, userStatus, userName) => {
    const isInactive = userStatus === 'inactive';
    const confirmTitle = isInactive ? 'Permanently Delete User' : 'Deactivate User';
    const confirmMessage = isInactive
      ? `Are you sure you want to permanently delete "${userName}"? This action cannot be undone.`
      : `Are you sure you want to deactivate "${userName}"?`;

    const confirmed = await Alert.confirm(confirmMessage, confirmTitle);
    if (!confirmed) return;

    try {
      const response = await userService.delete(userId);
      Alert.success(
        response.action === 'deleted'
          ? 'User permanently deleted!'
          : 'User deactivated successfully!'
      );
      if (users.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchUsers();
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const getProfileImageUrl = (user) => {
    if (user.profile_photo_base64) {
      return `data:image/jpeg;base64,${user.profile_photo_base64}`;
    }
    return userService.getProfilePhoto(user.user_id);
  };

  const columns = [
    {
      key: 'photo',
      label: 'Photo',
      width: '80px',
      render: (user) => (
        <div className="relative w-10 h-10">
          <img
            src={getProfileImageUrl(user)}
            alt={user.name}
            className="w-10 h-10 rounded-full object-cover border border-gray-200"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `data:image/svg+xml,${encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect width="40" height="40" fill="#e5e7eb"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-size="16">${user.name.charAt(0).toUpperCase()}</text></svg>`
              )}`;
            }}
          />
          <div
            className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
              user.is_online ? 'bg-emerald-500' : 'bg-gray-400'
            }`}
          />
        </div>
      ),
    },
    {
      key: 'name',
      label: 'Name',
      render: (user) => (
        <button
          onClick={() => navigate(`/users/view/${user.user_id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {user.name}
        </button>
      ),
    },
    { key: 'email', label: 'Email' },
    { key: 'contact', label: 'Contact' },
    { key: 'city', label: 'Branch' },
    { key: 'role_name', label: 'Role' },
    {
      key: 'status',
      label: 'Status',
      render: (user) => <StatusBadge status={user.status} />,
    },
    { key: 'created_by', label: 'Created By' },
  ];

  const getActions = (user) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/users/view/${user.user_id}`),
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/users/edit/${user.user_id}`),
      visible: canManageUsers,
    },
    {
      label: user.status === 'inactive' ? 'Permanently Delete' : 'Deactivate',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(user.user_id, user.status, user.name),
      visible: canManageUsers,
    },
  ];

  return (
    <PageLayout
      title="Users"
      icon={Users}
      actions={
        <Button icon={Plus} onClick={() => navigate('/users/add')}>
          Add User
        </Button>
      }
    >
      <FilterPanel
        filters={[
          { key: 'search', label: 'Search', placeholder: 'Name or email...' },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ],
          },
          {
            key: 'role_id',
            label: 'Role',
            type: 'select',
            options: roles.map((r) => ({ value: r.id, label: r.role_name })),
          },
        ]}
        values={filterValues}
        onChange={setFilterValues}
        onClear={() => setFilterValues({ search: '', status: '', role_id: '' })}
      />

      <DataTable
        columns={columns}
        data={users}
        loading={loading}
        emptyMessage="No users found"
        rowKey="user_id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default UserList;
